import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import { listInterviewSlots, getCandidateProfile } from '../graphql/queries';
import { updateInterviewSlot } from '../graphql/mutations';
import { isValidTokenFormat, TOKEN_STATUS } from '../utils/tokenUtils';
import InterviewRefactored from './InterviewRefactored'; // Interview component

/**
 * Public Interview Access Point
 * 
 * This component handles public interview access using signed tokens.
 * It validates the access token and loads the interview session.
 */

const PublicInterview = () => {
  const { accessToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interviewSlot, setInterviewSlot] = useState(null);
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [isValidating, setIsValidating] = useState(true);
  const [showStartPage, setShowStartPage] = useState(true);
  const [isStartingInterview, setIsStartingInterview] = useState(false);
  
  // Demo interview name collection
  const [showNameInput, setShowNameInput] = useState(false);
  const [demoName, setDemoName] = useState('');

  const validateTokenAndLoadInterview = useCallback(async () => {
    try {
      setIsValidating(true);
      
      // Find the interview slot using the ID as access token
      let interviewSlotData = null;
      let nextToken = null;
      
      do {
        const response = await API.graphql(
          graphqlOperation(listInterviewSlots, {
            filter: {
              id: { eq: accessToken }
            },
            limit: 100,
            nextToken: nextToken
          })
        );
        
        const items = response.data.listInterviewSlots.items;
        if (items.length > 0) {
          interviewSlotData = items[0];
          break;
        }
        
        nextToken = response.data.listInterviewSlots.nextToken;
      } while (nextToken);

      if (!interviewSlotData) {
        setError('Interview not found or access token is invalid');
        setLoading(false);
        return;
      }

      // Check token status using existing status field
      if (interviewSlotData.status === TOKEN_STATUS.USED) {
        setError('This interview link has already been used and cannot be accessed again');
        setLoading(false);
        return;
      }

      if (interviewSlotData.status === TOKEN_STATUS.EXPIRED) {
        setError('This interview link has expired');
        setLoading(false);
        return;
      }      // Load candidate profile (handle registered, unregistered, and demo candidates)
      let candidateData = null;
      
      // First, check if this is a demo interview
      let meetingConfig = {};
      try {
        meetingConfig = JSON.parse(interviewSlotData.meetingConfig || '{}');
      } catch (parseError) {
        console.error('Error parsing meetingConfig:', parseError);
      }
      
      const isDemo = meetingConfig.isDemo === true || meetingConfig.interviewType === 'demo';
      const isDemoPlaceholder = interviewSlotData.candidateProfileID === 'demo-no-profile';
      
      if (isDemo || isDemoPlaceholder) {
        // 🎯 Demo interview - create in-memory candidate profile (no database lookup needed)
        console.log('🎯 Demo interview detected - creating in-memory candidate profile');
        candidateData = {
          id: `demo-${interviewSlotData.id}`,
          email: interviewSlotData.candidateEmail || 'demo@getscreened.in',
          fullName: '', // Will be set by user input
          private_Candidate: true,
          isPublic: false,
          workStatus: 'Demo Candidate',
          description: `Demo Interview for ${meetingConfig.jobTitle || 'Platform Demonstration'}`,
          isDemo: true,
          isUnregistered: true, // Treat as unregistered for compatibility
          meetingConfig: meetingConfig
        };
        console.log('✅ Demo candidate profile created, awaiting name input');
        
        // Show name input for demo interviews
        setShowNameInput(true);
      } else if (interviewSlotData.candidateProfileID) {
        // Registered candidate - load from CandidateProfile table
        const candidateResponse = await API.graphql(
          graphqlOperation(getCandidateProfile, {
            id: interviewSlotData.candidateProfileID
          })
        );
        candidateData = candidateResponse.data.getCandidateProfile;
      } else {
        // Unregistered candidate - extract from meetingConfig
        try {
          if (meetingConfig.isUnregistered) {
            // Create a minimal candidate profile object for unregistered candidates
            candidateData = {
              id: `unregistered-${interviewSlotData.id}`,
              email: interviewSlotData.candidateEmail,
              fullName: meetingConfig.candidateName || 'Anonymous Candidate',
              private_Candidate: true,
              isPublic: false,
              // Add other minimal fields as needed
              workStatus: 'Unregistered Candidate',
              description: `Interview for ${meetingConfig.jobTitle || 'Private Screening'}`,
              // Mark as unregistered for special handling
              isUnregistered: true,
              meetingConfig: meetingConfig
            };
          }
        } catch (parseError) {
          console.error('Error parsing meetingConfig:', parseError);
        }
      }

      if (!candidateData) {
        setError('Candidate information not found');
        setLoading(false);
        return;
      }

      // Extract numberOfQuestions from meetingConfig for dynamic timer calculation
      let numberOfQuestions = 10; // Default to 5 questions
      try {
        const meetingConfig = JSON.parse(interviewSlotData.meetingConfig || '{}');
        if (meetingConfig.numberOfQuestions && typeof meetingConfig.numberOfQuestions === 'number') {
          numberOfQuestions = meetingConfig.numberOfQuestions;
          console.log(`📝 Interview configured for ${numberOfQuestions} questions`);
        }
      } catch (parseError) {
        console.warn('⚠️ Could not parse meetingConfig for numberOfQuestions, using default:', parseError);
      }
      
      // Store the data but don't start interview yet - wait for user to click start button
      setInterviewSlot(interviewSlotData);
      setCandidateProfile(candidateData);

    } catch (error) {
      console.error('Error validating token and loading interview:', error);
      setError('Failed to load interview. Please check your link and try again.');
    } finally {
      setLoading(false);
      setIsValidating(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) {
      setError('No access token provided');
      setLoading(false);
      return;
    }

    if (!isValidTokenFormat(accessToken)) {
      setError('Invalid access token format');
      setLoading(false);
      return;
    }

    validateTokenAndLoadInterview();
  }, [accessToken, validateTokenAndLoadInterview]);

  // Check if this is a demo interview
  const isDemoInterview = useMemo(() => {
    if (!interviewSlot || !interviewSlot.meetingConfig) return false;
    try {
      const config = JSON.parse(interviewSlot.meetingConfig);
      return config.interviewType === 'demo' || config.isDemo === true;
    } catch {
      return false;
    }
  }, [interviewSlot]);

  // Handle demo name submission
  const handleDemoNameSubmit = () => {
    if (demoName.trim()) {
      setCandidateProfile(prev => ({
        ...prev,
        fullName: demoName.trim()
      }));
      setShowNameInput(false);
    }
  };

  // Clean up localStorage when component unmounts
  useEffect(() => {
    return () => {
      localStorage.removeItem('publicInterviewMode');
    };
  }, []);
  const startInterviewSession = async () => {
    try {
      setIsStartingInterview(true);
      
      // Mark token as used by updating status to USED
      await API.graphql(
        graphqlOperation(updateInterviewSlot, {
          input: {
            id: interviewSlot.id,
            status: TOKEN_STATUS.USED
          }
        })
      );

      // Extract numberOfQuestions from meetingConfig and store it
      let numberOfQuestions = 5; // Default to 5 questions
      let jobData = null; // Job data for demo interviews
      try {
        const meetingConfig = JSON.parse(interviewSlot.meetingConfig || '{}');
        if (meetingConfig.numberOfQuestions && typeof meetingConfig.numberOfQuestions === 'number') {
          numberOfQuestions = meetingConfig.numberOfQuestions;
        }
        // 📋 Extract job data for demo interviews (sent to backend for relevant questions)
        if (meetingConfig.jobData) {
          jobData = meetingConfig.jobData;
          console.log('📋 Extracted jobData from meetingConfig:', jobData);
        }
      } catch (parseError) {
        console.warn('⚠️ Could not parse meetingConfig for numberOfQuestions, using default');
      }
      
      // Store candidate info in localStorage for the interview component
      localStorage.setItem('candidateProfileEmailID', candidateProfile.email);
      
      // Store complete candidate data for Interview.js to use
      localStorage.setItem('publicInterviewCandidateData', JSON.stringify(candidateProfile));
      localStorage.setItem('publicInterviewSlotData', JSON.stringify(interviewSlot));
      
      // Store numberOfQuestions for dynamic timer calculation
      localStorage.setItem('interviewNumberOfQuestions', numberOfQuestions.toString());
      console.log(`📝 Stored numberOfQuestions in localStorage: ${numberOfQuestions}`);
      
      // 📋 Store job data for demo interviews (backend uses this for question generation)
      if (jobData) {
        localStorage.setItem('interviewJobData', JSON.stringify(jobData));
        console.log('📋 Stored jobData in localStorage for backend');
      }
      
      // For unregistered candidates, use interview slot ID as profile ID
      const profileId = candidateProfile.isUnregistered 
        ? `unregistered-${interviewSlot.id}` 
        : candidateProfile.id;
      
      localStorage.setItem('candidateProfileID', profileId);
      localStorage.setItem('publicInterviewMode', 'true');
      localStorage.setItem('interviewSlotID', interviewSlot.id);
      
      // Store additional info for unregistered candidates
      if (candidateProfile.isUnregistered) {
        localStorage.setItem('isUnregisteredCandidate', 'true');
        localStorage.setItem('candidateFullName', candidateProfile.fullName);
        if (candidateProfile.meetingConfig) {
          localStorage.setItem('unregisteredCandidateConfig', JSON.stringify(candidateProfile.meetingConfig));
        }
      }

      // console.log('Public interview session started. Stored in localStorage:', {
      //   candidateEmail: candidateProfile.email,
      //   candidateFullName: candidateProfile.fullName,
      //   interviewSlotID: interviewSlot.id,
      //   isUnregistered: candidateProfile.isUnregistered || false
      // });

      // Hide the start page and show the interview
      setShowStartPage(false);

    } catch (error) {
      console.error('Error starting interview session:', error);
      setError('Failed to start interview session. Please try again.');
      setIsStartingInterview(false);
    }
  };

  if (loading || isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-400 mx-auto mb-6"></div>
          <p className="text-white text-lg">Validating your interview access...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-purple-900 to-indigo-900">
        <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-screen">
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-2xl p-8 text-center">
            <div className="text-red-500 mb-6">
              <svg className="mx-auto h-20 w-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>            <h2 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-8">{error}</p>
            <p className="text-gray-500 text-sm">Please contact the company that sent you this link for assistance.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!interviewSlot || !candidateProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg">Failed to load interview data</p>
        </div>
      </div>
    );
  }

  // Show start page with beautiful design
  if (showStartPage) {
    // Show name input for demo interviews first
    if (showNameInput && isDemoInterview) {
      return (
        <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-[#f6f6f8] dark:bg-[#101322] overflow-hidden p-4">
          {/* Background Gradient */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
            <div className="absolute -top-1/4 left-0 w-96 h-96 bg-blue-600/20 rounded-full filter blur-3xl opacity-50"></div>
            <div className="absolute -bottom-1/4 right-0 w-96 h-96 bg-purple-500/20 rounded-full filter blur-3xl opacity-40"></div>
            <div className="absolute top-1/2 left-1/3 w-80 h-80 bg-teal-500/20 rounded-full filter blur-3xl opacity-30"></div>
          </div>

          {/* Main Content */}
          <div className="relative z-10 flex w-full max-w-md flex-col items-center justify-center">
            <div className="w-full rounded-xl shadow-2xl flex flex-col bg-[#101322] border border-white/10">
              {/* Header with Logo */}
              <header className="flex items-center justify-center whitespace-nowrap border-b border-solid border-white/10 px-6 py-4 bg-white/5">
                <div className="flex items-center justify-center">
                  <img src={`${process.env.PUBLIC_URL}/deepscreened_logo.png`} alt="DeepScreened" className="h-8 object-contain" />
                </div>
              </header>

              {/* Page Content */}
              <main className="flex flex-col items-center p-6 text-center">
                {/* Page Heading */}
                <div className="flex w-full flex-col items-center gap-2 mb-6">
                  <p className="text-white text-xl font-bold leading-tight">Welcome to Demo Interview</p>
                  <p className="text-[#9da1b9] text-xs font-normal leading-normal">
                    Please enter your name to get started.
                  </p>
                </div>

                {/* Name Input */}
                <div className="w-full mb-4">
                  <input
                    type="text"
                    value={demoName}
                    onChange={(e) => setDemoName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleDemoNameSubmit()}
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-[#9da1b9] text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    autoFocus
                  />
                </div>

                {/* Continue Button */}
                <button
                  onClick={handleDemoNameSubmit}
                  disabled={!demoName.trim()}
                  className="flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-5 bg-blue-600 text-white text-sm font-bold hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </main>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-[#f6f6f8] dark:bg-[#101322] overflow-hidden p-4">
        {/* Background Gradient */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
          <div className="absolute -top-1/4 left-0 w-96 h-96 bg-blue-600/20 rounded-full filter blur-3xl opacity-50"></div>
          <div className="absolute -bottom-1/4 right-0 w-96 h-96 bg-purple-500/20 rounded-full filter blur-3xl opacity-40"></div>
          <div className="absolute top-1/2 left-1/3 w-80 h-80 bg-teal-500/20 rounded-full filter blur-3xl opacity-30"></div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 flex w-full max-w-md flex-col items-center justify-center">
          <div className="w-full rounded-xl shadow-2xl flex flex-col bg-[#101322] border border-white/10">
            {/* Header with Logo */}
            <header className="flex items-center justify-center whitespace-nowrap border-b border-solid border-white/10 px-6 py-4 bg-white/5">
              <div className="flex items-center justify-center">
                <img src={`${process.env.PUBLIC_URL}/deepscreened_logo.png`} alt="DeepScreened" className="h-8 object-contain" />
              </div>
            </header>

            {/* Page Content */}
            <main className="flex flex-col items-center px-8 py-10 text-center gap-8">
              {/* Page Heading */}
              <div className="flex w-full flex-col items-center gap-3">
                <h1 className="text-white text-2xl font-bold tracking-tight">Welcome to Your Interview</h1>
                <p className="text-[#9da1b9] text-sm font-normal leading-relaxed max-w-xs">
                  Hello {candidateProfile.fullName}, you are about to start your {isDemoInterview ? 'demo' : 'AI-powered'} interview. Please ensure you are in a quiet environment.
                </p>
              </div>

              {/* Interview Details */}
              <div className="flex items-center justify-center gap-2 rounded-full bg-white/5 px-4 py-2 text-xs font-medium text-[#9da1b9] border border-white/5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Estimated duration: {(() => {
                  try {
                    const config = JSON.parse(interviewSlot.meetingConfig || '{}');
                    const numQuestions = config.numberOfQuestions || 5;
                    const minutes = numQuestions * 3;
                    return `${minutes} minutes`;
                  } catch {
                    return '15 minutes';
                  }
                })()}</span>
              </div>

              {/* Action Area */}
              <div className="w-full flex flex-col items-center gap-4">
                <button
                  onClick={startInterviewSession}
                  disabled={isStartingInterview}
                  className="flex w-full max-w-xs cursor-pointer items-center justify-center overflow-hidden rounded-lg h-11 px-6 bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isStartingInterview ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Starting...
                    </span>
                  ) : (
                    <span>Start Interview</span>
                  )}
                </button>

                <p className="text-[#9da1b9] text-[10px] font-medium opacity-60">
                  {isDemoInterview ? 'Demo Mode: No recording will be saved' : 'Your session will be recorded for review'}
                </p>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  // Show the actual Interview component after start button is clicked
  return (
    <div className="min-h-screen">
      <InterviewRefactored />
    </div>
  );
};

export default PublicInterview;
